/*
 * Copyright (C) 2023 Nick Robinson
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

package uk.co.nickthecoder.glok.util

import uk.co.nickthecoder.glok.backend.Vector2
import kotlin.math.cos
import kotlin.math.sin

/**
 * A matrix for transposing 2D points.
 * The right column of the matrix is not stored, as it always 0,0,1.
 */
class Matrix(

    val m00: Float, val m01: Float,
    val m10: Float, val m11: Float,
    val m20: Float = 0f, val m21: Float = 0f
) {

    /**
     * Returns this matrix as a flat (1 dimension) array of floats of size 9.
     * Elements 2,5 and 8 are the un-stored right column of the 3x3 matrix and are always zero.
     */
    val asFlat3x3Array: Array<Float> by lazy {
        arrayOf<Float>(m00, m01, 0f, m10, m11, 0f, m20, m21, 1f)
    }

    fun rotate(radians: Float) = rotate(radians.toDouble())

    fun rotate(radians: Double): Matrix {
        val cos: Float = cos(radians).toFloat()
        val sin: Float = sin(radians).toFloat()

        return Matrix(
            m00 * cos + m10 * sin, m01 * cos + m11 * sin,
            m00 * - sin + m10 * cos, m01 * - sin + m11 * cos,
            m20, m21
        )
    }

    fun translate(x: Float, y: Float): Matrix {
        return Matrix(
            m00, m01,
            m10, m11,
            m00 * x + m10 * y + m20, m01 * x + m11 * y + m21
        )
    }

    fun scale(scale: Float) = scale(scale, scale)

    fun scale(x: Float, y: Float): Matrix {
        return Matrix(
            m00 * x, m01 * x,
            m10 * y, m11 * y,
            m20, m21
        )
    }

    operator fun times(right: Matrix): Matrix {

        return Matrix(
            m00 * right.m00 + m10 * right.m01, m01 * right.m00 + m11 * right.m01,
            m00 * right.m10 + m10 * right.m11, m01 * right.m10 + m11 * right.m11,
            m00 * right.m20 + m10 * right.m21 + m20, m01 * right.m20 + m11 * right.m21 + m21
        )

    }

    override fun toString(): String {
        return "\n[ $m00 , $m01\n  $m10 , $m11\n  $m20 , $m21 ]"
    }

    companion object {

        val IDENTITY = Matrix(1f, 0f, 0f, 1f)
        val ZERO = Matrix(0f, 0f, 0f, 0f)

        fun rotation(radians: Float) = rotation(radians.toDouble())

        fun rotation(radians: Double): Matrix {
            val cos = cos(radians).toFloat()
            val sin = sin(radians).toFloat()
            return Matrix(
                cos, sin,
                - sin, cos
            )
        }

        fun scale(x: Float, y: Float) = Matrix(
            x, 0f,
            0f, y
        )

        fun scale(scale: Float) = Matrix(
            scale, 0f,
            0f, scale
        )

        fun scale(scale: Vector2) = Matrix(
            scale.x, 0f,
            0f, scale.y
        )

        fun translation(x: Float, y: Float) = Matrix(
            1f, 0f,
            0f, 1f,
            x, y
        )
    }

}
